import { useEffect, useState } from 'react';
import { AlertCircle, Check, Info } from 'lucide-react';
import {
  Toaster as HotToaster,
  ToastBar,
  ToastIcon,
  resolveValue,
  type Toast,
  type ToasterProps,
  type ToastType,
} from 'react-hot-toast';

import { cn } from '../../utils/utils';

/**
 * react-hot-toast's four `type`s, mapped onto the alert tone scale. `blank` is
 * an untyped `toast('…')` call, which is a neutral notice rather than a status.
 */
/**
 * Success and error toasts use the quiz's answer colours (`answerTone.ts` in
 * the web app): a right answer and a saved change are the same "that worked"
 * signal, so they share the practice green, and a failure shares the answer's
 * destructive red, at the same 50% border / 10% tint.
 */
/**
 * The tone lives in the icon chip - a soft tint behind the glyph - not in a
 * tinted fill across the whole toast. The same card as the app's floating
 * banners (`PageBanner` on a phone): one surface, the theme's card, so a
 * toast reads as a calm note rather than a coloured alarm, and its text is
 * always on `bg-card`.
 */
const CHIP: Partial<Record<ToastType, string>> = {
  success: 'bg-success/15 text-success-emphasis',
  error: 'bg-destructive/10 text-destructive-emphasis',
  loading: 'bg-muted text-muted-foreground',
  blank: 'bg-info/10 text-info-emphasis',
};

const PHONE = '(max-width: 639.98px)';

/** Whether the viewport is a phone, kept live. */
function usePhone(): boolean {
  const query = () =>
    typeof window !== 'undefined' && window.matchMedia(PHONE).matches;
  const [phone, setPhone] = useState(query);
  useEffect(() => {
    const mql = window.matchMedia(PHONE);
    const on = () => setPhone(mql.matches);
    mql.addEventListener('change', on);
    return () => mql.removeEventListener('change', on);
  }, []);
  return phone;
}

function ChipIcon({ toast: t }: { toast: Toast }) {
  if (t.type === 'loading') return <ToastIcon toast={t} />;
  if (t.type === 'success')
    return <Check className='h-4 w-4' strokeWidth={2.5} />;
  if (t.type === 'error') return <AlertCircle className='h-4 w-4' />;
  return <Info className='h-4 w-4' />;
}

export function Toaster(props: ToasterProps) {
  // A phone: full width at the bottom, centred, in the thumb's reach, over
  // the safe area. Beside `sm`: the corner, as before.
  const phone = usePhone();
  return (
    <HotToaster
      position={phone ? 'bottom-center' : 'bottom-right'}
      gutter={8}
      // Above modals: the `Modal` primitive portals to <body> at z-50.
      containerStyle={{
        zIndex: 9999,
        ...(phone
          ? {
              left: 12,
              right: 12,
              bottom: 'max(12px, env(safe-area-inset-bottom))',
            }
          : {}),
      }}
      toastOptions={{
        duration: 4000,
        success: { duration: 3000 },
        error: { duration: 5000 },
        // Unset the library's inline defaults so the classes below are the
        // only thing painting the toast.
        style: { background: 'none', color: 'inherit', boxShadow: 'none' },
      }}
      {...props}
    >
      {t => {
        const message = resolveValue(t.message, t);
        // A plain message gets the tone's chip. A node (`notify.action`, an
        // error with a detail line) lays itself out, so it gets the chip only
        // when the toast has a real status.
        const chip = typeof message === 'string' || t.type !== 'blank';
        return (
          <ToastBar
            toast={t}
            style={{
              background: 'none',
              boxShadow: 'none',
              padding: 0,
              maxWidth: phone ? '100%' : 380,
              width: phone ? '100%' : undefined,
            }}
          >
            {() => (
              // It floats, so it needs elevation, and it sits in a
              // `pointer-events-none` container so it must opt back in to
              // being clickable. Opaque `bg-card`: a toast floats over other
              // UI, and a tinted surface let the chat composer and lesson
              // text show through it.
              <div className='border-border bg-card text-foreground shadow-elevation-2 pointer-events-auto flex w-full items-center gap-3 rounded-2xl border px-3 py-2.5 text-sm'>
                {chip && (
                  <span
                    aria-hidden='true'
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-full [&>div]:h-4 [&>div]:w-4',
                      CHIP[t.type] ?? CHIP.blank
                    )}
                  >
                    <ChipIcon toast={t} />
                  </span>
                )}
                {/* `resolveValue` rather than ToastBar's own `message` node:
                  that one arrives pre-wrapped in a margin this container does
                  not want. `t.ariaProps` (`role="status"` + `aria-live`)
                  normally ride on that node, so they are spread here - without
                  them no toast was ever announced. */}
                <div {...t.ariaProps} className='min-w-0 flex-1 font-medium'>
                  {message}
                </div>
              </div>
            )}
          </ToastBar>
        );
      }}
    </HotToaster>
  );
}
